<template>
  <div class="ia-shell">
    <!-- Content row: left=chat, right=action panel (full height, no topbar block) -->
    <div class="ia-content-row" :class="{ 'ia-content-row--split': activeSubmission !== null }">
      <aside class="ia-history-sidebar" :class="{ 'ia-history-sidebar--open': sidebarOpen }">
        <div class="ia-brand">
          <div class="ia-brand-badge">
            <img src="/icos/ot-transparente.png" alt="ObraTycoon" width="26" height="26" style="object-fit:contain;" />
          </div>
          <div class="ia-brand-text">
            <span class="ia-brand-name">Estagiario</span>
            <span class="ia-brand-sub">ObraTycoon</span>
          </div>
        </div>

        <button class="ia-new-chat" type="button" @click="startNewChat">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>
          Novo chat
        </button>

        <div class="ia-history-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
          <input v-model="historySearch" type="text" placeholder="Buscar no histórico" />
        </div>

        <div class="ia-history-list">
          <div class="ia-history-label">Histórico</div>
          <div
            v-for="chat in filteredChats"
            :key="chat.id"
            class="ia-history-item"
            :class="{ active: chat.id === activeChatId }"
            @click="selectChat(chat.id)"
          >
            <span class="ia-history-dot"></span>
            <span class="ia-history-text">
              <span class="ia-history-title">{{ chat.title }}</span>
              <span class="ia-history-meta">{{ formatHistoryTime(chat.updatedAt) }} · {{ chat.messages.length }} msg</span>
            </span>
            <button class="ia-history-delete" type="button" title="Apagar chat" @click.stop="deleteChat(chat.id)">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div v-if="filteredChats.length === 0" class="ia-history-empty">Nenhum chat encontrado.</div>
        </div>

        <!-- Recentemente apagados -->
        <div class="ia-trash-section">
          <button
            class="ia-trash-toggle"
            type="button"
            @click="trashOpen = !trashOpen"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            Recentemente apagados
            <span v-if="trashedSessions.length > 0" class="ia-trash-count">{{ trashedSessions.length }}</span>
            <svg class="ia-trash-chevron" :class="{ open: trashOpen }" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>

          <div v-if="trashOpen" class="ia-trash-list">
            <div v-if="trashedSessions.length === 0" class="ia-history-empty">Nenhum chat apagado.</div>
            <div
              v-for="chat in trashedSessions"
              :key="chat.id"
              class="ia-trash-item"
            >
              <span class="ia-trash-item-title">{{ chat.title }}</span>
              <span class="ia-trash-item-meta">{{ formatHistoryTime(chat.updatedAt) }}</span>
              <div class="ia-trash-item-actions">
                <button class="ia-trash-restore" type="button" title="Restaurar" @click="restoreChat(chat.id)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
                </button>
                <button class="ia-trash-purge" type="button" title="Apagar definitivamente" @click="purgeChat(chat.id)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- STT language selector -->
        <div class="ia-stt-config">
          <label class="ia-stt-label">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            Idioma do microfone
          </label>
          <select class="ia-stt-select" v-model="sttLang">
            <option value="pt-BR">🇧🇷 Português (BR)</option>
            <option value="pt-PT">🇵🇹 Português (PT)</option>
            <option value="en-US">🇺🇸 English (US)</option>
            <option value="en-GB">🇬🇧 English (UK)</option>
            <option value="es-ES">🇪🇸 Español</option>
            <option value="fr-FR">🇫🇷 Français</option>
            <option value="de-DE">🇩🇪 Deutsch</option>
            <option value="it-IT">🇮🇹 Italiano</option>
            <option value="zh-CN">🇨🇳 中文</option>
            <option value="ja-JP">🇯🇵 日本語</option>
          </select>
          <div v-if="!sttSupported" class="ia-stt-warn">Microfone não suportado neste navegador.</div>
        </div>
      </aside>

      <!-- Left: chat area -->
      <div class="ia-left">
        <!-- Back button floats in top-left without consuming layout space -->
        <button class="ia-back-btn" type="button" @click="goBack">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Voltar
        </button>
        <!-- Mobile: toggle histórico -->
        <button class="ia-history-toggle" type="button" @click="sidebarOpen = true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          Histórico
        </button>

        <!-- Body wrapper: contains fades + scrollable area -->
        <div class="ia-body-wrap">
          <div class="ia-fade ia-fade--top" v-if="canScrollUp && messages.length > 0"></div>

          <div
            class="ia-body"
            :class="{ 'ia-body--messages': messages.length > 0 }"
            ref="messagesBodyRef"
            @scroll="onBodyScroll"
          >
            <!-- Hero (only when no messages) -->
            <div v-if="messages.length === 0" class="ia-hero" :key="ganttReportMode">
              <!-- OFFLINE MODE hero -->
              <template v-if="ganttReportMode === 'offline'">
                <div class="ia-welcome-avatar">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                </div>
                <h1 class="ia-welcome-title">Relatório<br>de Obra</h1>
                <p class="ia-hero-desc">Gere automaticamente um relatório semanal a partir do Gantt, sem precisar de IA.</p>
                <div class="offline-report-form">
                  <div class="offline-report-field">
                    <label class="offline-report-label">Projeto</label>
                    <select v-model="offlineProjectId" class="offline-report-select" @change="offlineError = ''">
                      <option value="">Selecionar projeto...</option>
                      <option v-for="p in allProjects" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                  </div>
                  <div class="offline-report-field">
                    <label class="offline-report-label">Semana</label>
                    <select v-model="offlineWeekOffset" class="offline-report-select">
                      <option :value="-1">Semana passada</option>
                      <option :value="0">Semana atual</option>
                      <option :value="1">Próxima semana</option>
                    </select>
                  </div>
                  <p v-if="offlineError" class="offline-report-error">{{ offlineError }}</p>
                  <button
                    class="offline-report-btn"
                    type="button"
                    :disabled="!offlineProjectId || offlineGenerating"
                    @click="generateOfflineReport"
                  >
                    <svg v-if="!offlineGenerating" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                    <span class="ia-loading-dot" v-else></span>
                    {{ offlineGenerating ? 'Gerando...' : 'Gerar Relatório' }}
                  </button>
                </div>
              </template>

              <!-- IA MODE hero (default) -->
              <template v-else>
                <div class="ia-welcome-avatar">
                  <img src="@/assets/castor.png" alt="Estagiário" width="52" height="52" style="object-fit:contain;" />
                </div>
                <h1 class="ia-welcome-title">Como posso<br>ajudar?</h1>
                <p class="ia-hero-desc">Sou o Estagiário, seu assistente para gestão de obras. Tire dúvidas, analise dados e automatize tarefas do canteiro.</p>
                <div class="ia-hero-suggestions">
                  <button class="ia-suggestion-chip" type="button" @click="fillSuggestion('Criar relatório de hoje: ')">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    Criar relatório de hoje
                  </button>
                  <button class="ia-suggestion-chip" type="button" @click="fillSuggestion('Adicionar ao planejamento: ')">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4"/></svg>
                    Adicionar atividade ao planejamento
                  </button>
                  <button class="ia-suggestion-chip" type="button" @click="fillSuggestion('Ver o último relatório de ')">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
                    Ver um relatório
                  </button>
                </div>
              </template>
            </div>

            <!-- Messages list -->
            <div v-else class="ia-messages">
              <div
                v-for="msg in messages"
                :key="msg.id"
                class="ia-msg"
                :class="'ia-msg--' + msg.role"
              >
                <template v-if="msg.role === 'assistant'">
                  <div class="ia-bot-avatar">
                    <img src="@/assets/castor.png" alt="Estagiário" width="24" height="24" style="object-fit:contain;" />
                  </div>
                  <div class="ia-msg-body">
                    <div class="ia-msg-name">Estagiário <span class="ia-msg-tag">IA</span></div>
                    <div class="ia-msg-text">{{ msg.content }}</div>
                  </div>
                </template>
                <template v-else>
                  <div class="ia-user-bubble">{{ msg.content }}</div>
                </template>
              </div>

              <!-- Digitando... (enquanto a IA responde) -->
              <div v-if="isLoading" class="ia-msg ia-msg--assistant">
                <div class="ia-bot-avatar">
                  <img src="@/assets/castor.png" alt="Estagiário" width="24" height="24" style="object-fit:contain;" />
                </div>
                <div class="ia-msg-body">
                  <div class="ia-msg-name">Estagiário <span class="ia-msg-tag">IA</span></div>
                  <div class="ia-typing-dots" aria-label="Digitando..."><span></span><span></span><span></span></div>
                </div>
              </div>
            </div>
          </div>

          <div class="ia-fade ia-fade--bottom" v-if="canScrollDown && messages.length > 0"></div>
        </div>

        <!-- Input bar -->
        <div class="ia-input-area">

          <!-- ── CONFIRM: Sim, salvar / Não, editar / Salvar rascunho ── -->
          <div v-if="chatState === 'confirm'" class="ia-action-bar">
            <button class="ia-action-btn ia-action-btn--save" type="button" @click="confirmSave">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Sim, salvar
            </button>
            <button class="ia-action-btn ia-action-btn--edit" type="button" @click="confirmEdit">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Não, editar
            </button>
          </div>

          <!-- ── GANTT CONFIRM: Sim, adicionar / Cancelar ── -->
          <div v-else-if="chatState === 'gantt-confirm'" class="ia-action-bar">
            <button class="ia-action-btn ia-action-btn--save" type="button" :disabled="ganttSaving" @click="confirmAddGanttTasks">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              {{ ganttSaving ? 'Adicionando…' : 'Sim, adicionar ao planejamento' }}
            </button>
            <button class="ia-action-btn ia-action-btn--back" type="button" :disabled="ganttSaving" @click="cancelGanttTasks">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              Cancelar
            </button>
          </div>

          <!-- ── VIEW: Gerar PDF / Voltar para IA ── -->
          <div v-else-if="chatState === 'view'" class="ia-action-bar">
            <button class="ia-action-btn ia-action-btn--pdf" type="button" @click="exportPdf">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Gerar PDF
            </button>
            <button class="ia-action-btn ia-action-btn--edit" type="button" @click="editActiveSubmission">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Editar relatório
            </button>
            <button class="ia-action-btn ia-action-btn--back" type="button" @click="resetToIdle">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Voltar para IA
            </button>
          </div>

          <!-- ── FILLING: option chips + Escrever + Salvar rascunho ── -->
          <div v-else-if="chatState === 'filling' && !isWriteMode" class="ia-question-bar">
            <button
              v-for="opt in questionOptions"
              :key="opt.value"
              class="ia-opt-btn"
              type="button"
              @click="handleOptionClick(opt)"
            >{{ opt.label }}</button>
            <button class="ia-write-btn" type="button" @click="enterWriteMode">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Escrever
            </button>
          </div>

          <!-- ── TEXTAREA (idle or filling+write) ── -->
          <div v-if="showTextarea" class="ia-input-wrap">
            <input ref="fileInputRef" type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" style="display:none" @change="onFileChange" />
            <button class="ia-tool-btn" type="button" title="Anexar arquivo" @click="fileInputRef?.click()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
            </button>
            <button
              v-if="isRecording || !textareaFocused"
              class="ia-tool-btn"
              :class="{ 'ia-tool-btn--recording': isRecording }"
              type="button"
              :title="isRecording ? 'Parar gravação' : 'Gravar áudio'"
              @click="toggleRecording"
            >
              <svg v-if="!isRecording" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
            </button>
            <textarea
              ref="textareaRef"
              v-model="inputText"
              class="ia-textarea"
              :placeholder="isRecording ? 'Gravando áudio...' : questionPlaceholder"
              :disabled="isRecording"
              rows="1"
              @keydown.enter.exact.prevent="sendMessage"
              @input="autoResize"
              @focus="textareaFocused = true"
              @blur="textareaFocused = false"
            ></textarea>
            <button
              class="ia-send-btn"
              :class="{ 'ia-send-btn--active': inputText.trim() || attachments.length > 0 }"
              type="button"
              :disabled="!inputText.trim() && attachments.length === 0"
              title="Enviar (Enter)"
              @click="sendMessage"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </button>
          </div>

          <template v-if="showTextarea">
            <div v-if="attachments.length > 0" class="ia-attachments">
              <div v-for="(f, i) in attachments" :key="i" class="ia-attachment">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                <span>{{ f.name }}</span>
                <button type="button" class="ia-attachment-remove" @click="removeAttachment(i)">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
            <div v-if="isRecording" class="ia-recording-bar">
              <span class="ia-rec-dot"></span>
              <span>Gravando... {{ recordingTime }}s</span>
              <button type="button" class="ia-rec-cancel" @click="cancelRecording">Cancelar</button>
            </div>
          </template>

          <p class="ia-disclaimer">ObraTycoon IA pode cometer erros. Verifique informações importantes.</p>
        </div>
      </div>

      <!-- Right: submission preview panel (full height) -->
      <div v-if="activeSubmission !== null" class="ia-side-panel">
        <div class="ia-panel-header">
          <div class="ia-panel-header-title">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span>Rascunho de Relatório</span>
          </div>
          <button class="ia-panel-close" type="button" @click="closePanel">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="ia-panel-body">
          <div class="ia-panel-field">
            <label class="ia-panel-label">Título</label>
            <div class="ia-panel-value">{{ activeSubmission.title || '—' }}</div>
          </div>
          <div class="ia-panel-row">
            <div class="ia-panel-field">
              <label class="ia-panel-label">Categoria</label>
              <div class="ia-panel-value">
                <span class="ia-panel-badge" :class="'ia-panel-badge--' + activeSubmission.category">{{ categoryLabel(activeSubmission.category) }}</span>
              </div>
            </div>
            <div class="ia-panel-field">
              <label class="ia-panel-label">Data</label>
              <div class="ia-panel-value">{{ formatDate(activeSubmission.reportDate) }}</div>
            </div>
          </div>
          <div class="ia-panel-field" v-if="activeSubmission.attraction">
            <label class="ia-panel-label">Frente de trabalho</label>
            <div class="ia-panel-value">{{ activeSubmission.attraction }}</div>
          </div>
          <div class="ia-panel-field" v-if="activeSubmission.collaborators.length">
            <label class="ia-panel-label">Colaboradores</label>
            <div class="ia-panel-tags">
              <span v-for="name in activeSubmission.collaborators" :key="name" class="ia-panel-tag">{{ name }}</span>
            </div>
          </div>
          <div class="ia-panel-field" v-if="activeSubmission.activities.length">
            <label class="ia-panel-label">Atividades</label>
            <div class="ia-panel-list">
              <div v-for="act in activeSubmission.activities" :key="act.id" class="ia-panel-list-item">
                <span class="ia-panel-list-dot"></span>
                <span>{{ act.description }}</span>
              </div>
            </div>
          </div>
          <div class="ia-panel-field" v-if="activeSubmission.tools.length">
            <label class="ia-panel-label">Ferramentas / EPI</label>
            <div class="ia-panel-list">
              <div v-for="tool in activeSubmission.tools" :key="tool.id" class="ia-panel-list-item">
                <span class="ia-panel-list-dot"></span>
                <span>{{ tool.text }}</span>
              </div>
            </div>
          </div>
          <div class="ia-panel-field" v-if="activeSubmission.observations.length">
            <label class="ia-panel-label">Observações</label>
            <div class="ia-panel-list">
              <div v-for="obs in activeSubmission.observations" :key="obs.id" class="ia-panel-list-item">
                <span class="ia-panel-list-dot"></span>
                <span>{{ obs.text }}</span>
              </div>
            </div>
          </div>
          <div class="ia-panel-author">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <span>{{ activeSubmission.author.name }}</span>
          </div>
        </div>

      </div>

      <!-- Right: gantt tasks preview panel -->
      <div v-if="ganttPanelVisible" class="ia-side-panel">
        <div class="ia-panel-header">
          <div class="ia-panel-header-title">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
            <span>Atividades para o Planejamento</span>
          </div>
          <button class="ia-panel-close" type="button" @click="cancelGanttTasks">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="ia-panel-body">
          <div class="ia-panel-field">
            <label class="ia-panel-label">Projeto</label>
            <div class="ia-panel-value">{{ ganttTargetProjectName || '—' }}</div>
          </div>
          <div v-for="grp in ganttTasksByArea" :key="grp.area" class="ia-panel-field">
            <label class="ia-panel-label">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-1px;margin-right:4px"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              {{ grp.area }}
            </label>
            <div v-for="(t, i) in grp.tasks" :key="grp.area + '-' + i" class="ia-gantt-task">
              <div class="ia-gantt-task-title">{{ t.title }}</div>
              <div class="ia-gantt-task-meta">
                <span class="ia-gantt-chip" :class="{ 'ia-gantt-chip--nodate': !t.startDate }">
                  {{ t.startDate ? formatDate(t.startDate) : 'Data não informada' }}
                </span>
                <span class="ia-gantt-chip">{{ t.durationDays }} {{ t.durationDays > 1 ? 'dias' : 'dia' }}</span>
                <span class="ia-gantt-chip ia-gantt-chip--assignee">{{ t.assigneeName || currentUserName }}</span>
              </div>
            </div>
          </div>
          <p class="ia-gantt-note">Áreas que ainda não existirem serão criadas como grupos no Gantt. Atividades sem data entram na lista "Sem data definida" do planejamento.</p>
        </div>
      </div>

      <!-- Mobile backdrop (side panel) -->
      <div v-if="activeSubmission !== null || ganttPanelVisible" class="ia-panel-backdrop" @click="activeSubmission !== null ? closePanel() : cancelGanttTasks()"></div>
      <!-- Mobile backdrop (sidebar drawer) -->
      <div v-if="sidebarOpen" class="ia-sidebar-backdrop" @click="sidebarOpen = false"></div>
    </div>
  </div>

  <!-- Delete confirmation dialog -->
  <Teleport to="body">
    <div v-if="deletePendingId" class="ia-delete-backdrop" @click.self="deletePendingId = null">
      <div class="ia-delete-dialog">
        <div class="ia-delete-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
        </div>
        <p class="ia-delete-title">Deseja mesmo apagar este chat?</p>
        <p class="ia-delete-sub">Ele será movido para "Recentemente apagados" e poderá ser restaurado.</p>
        <div class="ia-delete-actions">
          <button class="ia-delete-cancel" type="button" @click="deletePendingId = null">Cancelar</button>
          <button class="ia-delete-confirm" type="button" @click="confirmDelete">Apagar</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { submissionsStore } from '@/store/submissions'
import { projectsStore } from '@/store/projects'
import { sessionStore } from '@/store/session'
import { systemStore } from '@/store/system'
import { apiFetch } from '@/store/api'
import { annotationsStore } from '@/store/annotations'
import { useReportExport } from '@/composables/useReportExport'
import { newId } from '@/utils/id'
import type { Submission } from '@/store/submissions'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
}

interface ChatSession {
  id: string
  title: string
  messages: ChatMessage[]
  createdAt: string
  updatedAt: string
  // Memória rolante: resumo das mensagens antigas já "dobradas" + quantas foram.
  // Só o resumo + as mensagens após memoryCount vão para a IA (economia de tokens).
  memory?: string
  memoryCount?: number
}

interface EditingContext {
  mode: 'draft' | 'existing'
  submissionId: string
  title: string
  project: string
  date: string
  category: 'relatorio' | 'ocorrido'
}

type ChatState = 'idle' | 'filling' | 'confirm' | 'view' | 'gantt-confirm'
type MissingField = 'project' | 'type' | 'activities'
type FlowType = 'create' | 'view' | 'add_task' | null

interface GanttTaskDraft {
  title: string
  area: string | null
  startDate: string | null
  durationDays: number
  assigneeUsername: string | null
  assigneeName: string | null
}

interface EditResponse {
  changes: {
    reportDate?: string
    attraction?: string
    category?: 'relatorio' | 'ocorrido'
    activities?: string[]
    observations?: string[]
    collaborators?: string[]
    tools?: string[]
  }
}

interface AnalyzeResponse {
  intent: 'create' | 'view' | 'copy' | 'chat' | 'add_task' | 'create_ticket'
  formData?: {
    projectName: string | null
    type: 'relatorio' | 'ocorrido' | null
    activities: Array<{ id: string; description: string; images: string[] }>
    observations: Array<{ id: string; text: string; images: string[] }>
    collaborators: string[]
    date: string
  }
  viewQuery?: { projectName: string | null; date: string | null; type: string | null }
  copyQuery?: { projectName: string | null; date: string | null; type: string | null }
  missing: (MissingField | 'tasks')[]
  projects?: Array<{ id: string; name: string }>
  chatMessage?: string
  suggest_annotate?: boolean
  projectName?: string | null
  ganttTasks?: GanttTaskDraft[]
  ticket?: { title: string; category: string; description: string; priority: string }
  memory?: { summary: string; folded: number } | null
}

export default defineComponent({
  name: 'IAChatPanel',
  setup() {
    const router = useRouter()
    const goBack = () => router.back()

    // ── MOBILE SIDEBAR ──
    const sidebarOpen = ref(false)

    // ── INPUT ──
    const inputText = ref('')
    const textareaRef = ref<HTMLTextAreaElement | null>(null)
    const fileInputRef = ref<HTMLInputElement | null>(null)
    const attachments = ref<File[]>([])
    const isRecording = ref(false)
    const recordingTime = ref(0)
    let recordingInterval: ReturnType<typeof setInterval> | null = null
    let msgCounter = 0

    // ── CHAT ──
    const messages = ref<ChatMessage[]>([])
    const chatSessions = ref<ChatSession[]>([])
    const activeChatId = ref('')
    const historySearch = ref('')
    const messagesBodyRef = ref<HTMLDivElement | null>(null)
    const canScrollUp = ref(false)
    const canScrollDown = ref(false)
    const isLoading = ref(false)

    // ── FLOW STATE ──
    const chatState = ref<ChatState>('idle')
    const currentFlow = ref<FlowType>(null)
    const activeSubmission = ref<Submission | null>(null)
    const pendingFormData = ref<Record<string, unknown>>({})
    const missingQueue = ref<MissingField[]>([])
    const currentMissingField = ref<MissingField | null>(null)
    const questionOptions = ref<Array<{ label: string; value: string }>>([])
    const isWriteMode = ref(false)
    const allProjects = ref<Array<{ id: string; name: string }>>([])
    const editingContext = ref<EditingContext | null>(null)
    const pendingAnnotationText = ref<string | null>(null)

    // ── GANTT / PLANEJAMENTO (add_task) ──
    const pendingGanttTasks = ref<GanttTaskDraft[]>([])
    const ganttTargetProjectName = ref<string | null>(null)
    const ganttSaving = ref(false)

    const ganttTasksByArea = computed(() => {
      const map = new Map<string, GanttTaskDraft[]>()
      for (const t of pendingGanttTasks.value) {
        const key = t.area || 'Sem área definida'
        const list = map.get(key) || []
        list.push(t)
        map.set(key, list)
      }
      return Array.from(map.entries()).map(([area, tasks]) => ({ area, tasks }))
    })

    const ganttPanelVisible = computed(() =>
      chatState.value === 'gantt-confirm' && pendingGanttTasks.value.length > 0
    )

    const currentUserName = computed(() =>
      sessionStore.state.user?.name || sessionStore.state.user?.username || 'Você'
    )

    // ── CHAVES DE STORAGE ──
    // (rascunhos não são mais persistidos entre aberturas: o painel sempre abre
    // em uma conversa nova, como um chatbot comum; fluxos vivem só na sessão)
    const historyKey = computed(() =>
      `ia-chat-history-${sessionStore.state.user?.systemId || 'unknown'}-${sessionStore.state.user?.username || 'anon'}`
    )

    const closePanel = () => {
      activeSubmission.value = null
      chatState.value = 'idle'
      editingContext.value = null
      currentFlow.value = null
      pendingFormData.value = {}
      missingQueue.value = []
      currentMissingField.value = null
      questionOptions.value = []
      isWriteMode.value = false
      pendingGanttTasks.value = []
      ganttTargetProjectName.value = null
    }

    // ── PDF EXPORT ──
    const pdfDocs = ref<Submission[]>([])
    const pdfSelected = ref<string[]>([])
    const sysColor = computed(() => {
      const cfg = systemStore.state.config as Record<string, unknown>
      const colors = cfg.colors as Record<string, string> | undefined
      return colors?.report || colors?.primary || '#1E40AF'
    })
    const sysName = computed(() => systemStore.state.config.siteName || 'ObraTycoon')
    const ganttReportMode = computed(() => systemStore.state.config.ganttReportMode || 'ia')

    // ── OFFLINE REPORT MODE ──
    const offlineProjectId = ref('')
    const offlineWeekOffset = ref(0)
    const offlineGenerating = ref(false)
    const offlineError = ref('')

    const generateOfflineReport = async () => {
      if (!offlineProjectId.value) return
      offlineGenerating.value = true
      offlineError.value = ''
      try {
        const data = await apiFetch<{
          title: string; category: 'relatorio' | 'ocorrido'; attraction: string; reportDate: string
          activities: Array<{ id: string; description: string; images: string[] }>
          observations: Array<{ id: string; text: string; images: string[] }>
          collaborators: string[]
          tools: Array<{ id: string; text: string; images: string[] }>
        }>(`/projects/${encodeURIComponent(offlineProjectId.value)}/gantt-report`, {
          method: 'POST',
          body: JSON.stringify({ weekOffset: offlineWeekOffset.value })
        })
        const newSub: Submission = {
          id: newId('draft-offline'),
          title: data.title,
          category: data.category,
          attraction: data.attraction,
          reportDate: data.reportDate,
          collaborators: data.collaborators || [],
          tools: data.tools || [],
          activities: data.activities || [],
          observations: data.observations || [],
          reportLanguage: 'pt',
          status: 'rascunho',
          author: { name: '', role: 'user', username: '', uid: '', avatar: '', roleId: '', subordinateUsernames: [], hasSubordinates: false },
          createdAt: new Date().toISOString()
        }
        activeSubmission.value = newSub
        chatState.value = 'confirm'
        pushMessage('assistant', `Relatório gerado a partir do Gantt de "${data.attraction}". Confira no painel e salve quando estiver pronto.`)
      } catch (err) {
        offlineError.value = err instanceof Error ? err.message : 'Erro ao gerar relatório.'
      } finally {
        offlineGenerating.value = false
      }
    }
    const { exportSelectedDocsAsPdf } = useReportExport({
      selectedDocs: pdfSelected,
      documentsItems: pdfDocs,
      formatDateOnly: (v: string) => { const [y, m, d] = v.split('-'); return `${d}/${m}/${y}` },
      siteName: sysName,
      systemColor: sysColor
    })

    const makeChatId = () => newId('chat')

    const summarizeTitle = (items: ChatMessage[]) => {
      const firstUser = items.find((msg) => msg.role === 'user' && msg.content.trim())
      if (!firstUser) return 'Novo chat'
      const clean = firstUser.content.replace(/\s+/g, ' ').trim()
      return clean.length > 44 ? `${clean.slice(0, 44)}...` : clean
    }

    const saveChatHistory = () => {
      const payload = chatSessions.value.slice(0, 30)
      // always keep a local copy as fallback
      try { localStorage.setItem(historyKey.value, JSON.stringify(payload)) } catch { /* ignore */ }
      // sync to server (fire-and-forget)
      apiFetch('/ia/history', {
        method: 'POST',
        body: JSON.stringify({ sessions: payload })
      }).catch(() => { /* non-fatal — localStorage backup already saved */ })
    }

    // Sessão é criada "lazy": só entra na sidebar (e no servidor) quando a
    // conversa tem pelo menos uma mensagem — abrir e fechar não gera lixo.
    const syncActiveChat = () => {
      if (!activeChatId.value || messages.value.length === 0) return
      const now = new Date().toISOString()
      const existing = chatSessions.value.find((chat) => chat.id === activeChatId.value)
      if (existing) {
        existing.messages = [...messages.value]
        existing.title = summarizeTitle(messages.value)
        existing.updatedAt = now
      } else {
        chatSessions.value.unshift({
          id: activeChatId.value,
          title: summarizeTitle(messages.value),
          messages: [...messages.value],
          createdAt: now,
          updatedAt: now
        })
      }
      chatSessions.value = [...chatSessions.value].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      saveChatHistory()
    }

    // Conversa nova em branco (não registrada na sidebar até a 1ª mensagem)
    const beginFreshChat = () => {
      activeChatId.value = makeChatId()
      messages.value = []
      nextTick(onBodyScroll)
    }

    const parseSessions = (raw: unknown[]): ChatSession[] =>
      raw
        .filter((item) => item && typeof (item as ChatSession).id === 'string' && Array.isArray((item as ChatSession).messages))
        .map((item) => {
          const s = item as ChatSession
          return {
            id: String(s.id),
            title: String(s.title || 'Novo chat'),
            messages: s.messages.filter((msg) => msg && (msg.role === 'user' || msg.role === 'assistant')),
            createdAt: String(s.createdAt || new Date().toISOString()),
            updatedAt: String(s.updatedAt || s.createdAt || new Date().toISOString()),
            memory: typeof s.memory === 'string' ? s.memory : '',
            memoryCount: Number(s.memoryCount || 0)
          }
        })
        .filter((s) => s.messages.length > 0) // descarta sessões vazias legadas
        .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))

    const applyLoadedSessions = (sessions: ChatSession[]) => {
      chatSessions.value = sessions
      // Sempre abre em conversa nova; sessões antigas ficam na sidebar.
      beginFreshChat()
    }

    const loadChatHistory = async () => {
      // Try server first (shared across devices)
      try {
        const data = await apiFetch<{ sessions: unknown[] }>('/ia/history')
        const sessions = parseSessions(data.sessions || [])
        // If server has data, use it and update local cache
        if (sessions.length > 0) {
          try { localStorage.setItem(historyKey.value, JSON.stringify(sessions)) } catch { /* ignore */ }
          applyLoadedSessions(sessions)
          return
        }
      } catch { /* server unavailable — fall through to localStorage */ }

      // Fallback: localStorage (e.g. offline or first load before server sync)
      try {
        const saved = localStorage.getItem(historyKey.value)
        const parsed: unknown[] = saved ? JSON.parse(saved) : []
        applyLoadedSessions(Array.isArray(parsed) ? parseSessions(parsed) : [])
      } catch {
        applyLoadedSessions([])
      }
    }

    const clearFlowState = (clearPanel = true) => {
      chatState.value = 'idle'
      currentFlow.value = null
      if (clearPanel) activeSubmission.value = null
      pendingFormData.value = {}
      missingQueue.value = []
      currentMissingField.value = null
      questionOptions.value = []
      isWriteMode.value = false
      editingContext.value = null
    }

    const startNewChat = () => {
      syncActiveChat()
      clearFlowState()
      inputText.value = ''
      attachments.value = []
      sidebarOpen.value = false
      beginFreshChat()
      nextTick(() => textareaRef.value?.focus())
    }

    // ── TRASH (recentemente apagados) ──
    const trashKey = computed(() =>
      `ia-chat-trash-${sessionStore.state.user?.systemId || 'unknown'}-${sessionStore.state.user?.username || 'anon'}`
    )
    const trashedSessions = ref<ChatSession[]>([])
    const trashOpen = ref(false)

    const loadTrash = () => {
      try {
        const raw = localStorage.getItem(trashKey.value)
        if (raw) trashedSessions.value = JSON.parse(raw) as ChatSession[]
      } catch { /* ignore */ }
    }

    const saveTrash = () => {
      try { localStorage.setItem(trashKey.value, JSON.stringify(trashedSessions.value.slice(0, 20))) } catch { /* ignore */ }
    }

    // ── DELETE with confirmation ──
    const deletePendingId = ref<string | null>(null)

    const deleteChat = (id: string) => {
      deletePendingId.value = id
    }

    const confirmDelete = () => {
      const id = deletePendingId.value
      if (!id) return
      deletePendingId.value = null
      const chat = chatSessions.value.find((c: ChatSession) => c.id === id)
      if (chat) {
        trashedSessions.value = [chat, ...trashedSessions.value].slice(0, 20)
        saveTrash()
      }
      chatSessions.value = chatSessions.value.filter((c: ChatSession) => c.id !== id)
      saveChatHistory()
      trashOpen.value = true
      if (activeChatId.value === id) {
        beginFreshChat()
      }
    }

    const restoreChat = (id: string) => {
      const chat = trashedSessions.value.find((c: ChatSession) => c.id === id)
      if (!chat) return
      trashedSessions.value = trashedSessions.value.filter((c: ChatSession) => c.id !== id)
      saveTrash()
      chatSessions.value = [chat, ...chatSessions.value].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      saveChatHistory()
      activeChatId.value = chat.id
      messages.value = [...chat.messages]
    }

    const purgeChat = (id: string) => {
      trashedSessions.value = trashedSessions.value.filter((c: ChatSession) => c.id !== id)
      saveTrash()
    }

    const selectChat = (id: string) => {
      if (id === activeChatId.value) return
      syncActiveChat()
      const chat = chatSessions.value.find((item) => item.id === id)
      if (!chat) return
      clearFlowState()
      activeChatId.value = chat.id
      messages.value = [...chat.messages]
      inputText.value = ''
      attachments.value = []
      sidebarOpen.value = false
      scrollToBottom()
    }

    const filteredChats = computed(() => {
      const query = historySearch.value.trim().toLowerCase()
      const items = [...chatSessions.value].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      if (!query) return items
      return items.filter((chat) =>
        chat.title.toLowerCase().includes(query) ||
        chat.messages.some((msg) => msg.content.toLowerCase().includes(query))
      )
    })

    const formatHistoryTime = (iso: string) => {
      const date = new Date(iso)
      if (Number.isNaN(date.getTime())) return ''
      const today = new Date()
      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      }
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    }

    // ── HELPERS ──
    const makeFormId = () => newId()

    const onBodyScroll = () => {
      const el = messagesBodyRef.value
      if (!el) return
      canScrollUp.value = el.scrollTop > 10
      canScrollDown.value = el.scrollTop < el.scrollHeight - el.clientHeight - 10
    }

    const scrollToBottom = () => {
      nextTick(() => {
        const el = messagesBodyRef.value
        if (!el) return
        el.scrollTop = el.scrollHeight
        onBodyScroll()
      })
    }

    // Rola para o fim quando o indicador "digitando" aparece
    watch(isLoading, (v: boolean) => { if (v) scrollToBottom() })

    const pushMessage = (role: 'user' | 'assistant', content: string) => {
      messages.value.push({ id: newId(`msg-${++msgCounter}`), role, content })
      syncActiveChat()
      scrollToBottom()
    }

    const categoryLabel = (cat: string) => {
      const map: Record<string, string> = {
        relatorio: 'Relatório Diário',
        ocorrido: 'Registro de Ocorrência',
        dialogo: 'Diálogo de Segurança',
        permissao: 'Permissão de Trabalho',
      }
      return map[cat] || cat
    }

    const formatDate = (dateStr: string) => {
      if (!dateStr) return '—'
      const [y, m, d] = dateStr.split('-')
      if (!y || !m || !d) return dateStr
      return `${d}/${m}/${y}`
    }

    const showTextarea = computed(() =>
      chatState.value === 'idle' || (chatState.value === 'filling' && isWriteMode.value)
    )

    const textareaFocused = ref(false)

    const questionPlaceholder = computed(() => {
      if (currentMissingField.value === 'project') return 'Digite o nome do projeto...'
      if (currentMissingField.value === 'type') return 'Relatório diário ou registro de ocorrência...'
      if (currentMissingField.value === 'activities') return 'Descreva as atividades do dia...'
      return 'Do que precisa?'
    })

    // ── FORM BUILDER ──
    const buildAndConfirm = () => {
      const data = pendingFormData.value
      const category = (data.type as 'relatorio' | 'ocorrido') || 'relatorio'
      const dateStr = (data.date as string) || new Date().toISOString().slice(0, 10)
      const [y, m, d] = dateStr.split('-')
      const typeLabel = category === 'relatorio' ? 'Relatório Diário' : 'Registro de Ocorrência'

      activeSubmission.value = {
        id: makeFormId(),
        title: `${typeLabel} — ${d}/${m}/${y}`,
        category,
        attraction: (data.project as string) || '',
        reportDate: dateStr,
        collaborators: (data.collaborators as string[]) || [],
        tools: [],
        activities: (data.activities as Array<{ id: string; description: string; images: string[] }>) || [],
        observations: (data.observations as Array<{ id: string; text: string; images: string[] }>) || [],
        reportLanguage: 'pt',
        status: 'rascunho',
        author: sessionStore.state.user!,
        createdAt: new Date().toISOString()
      }
      setEditingContextFromActive('draft')
      chatState.value = 'confirm'
      pushMessage('assistant', 'Aqui está o rascunho. Confira ao lado — está tudo certo?')
    }

    // ── MISSING FIELD FLOW ──
    const askNextMissing = () => {
      if (missingQueue.value.length === 0) {
        buildAndConfirm()
        return
      }
      const field = missingQueue.value[0]
      currentMissingField.value = field
      chatState.value = 'filling'

      if (field === 'project') {
        questionOptions.value = allProjects.value.map((p: { id: string; name: string }) => ({ label: p.name, value: p.name }))
        isWriteMode.value = false
        pushMessage('assistant', 'Qual é o projeto deste relatório?')
      } else if (field === 'type') {
        questionOptions.value = [
          { label: 'Relatório Diário', value: 'relatorio' },
          { label: 'Registro de Ocorrência', value: 'ocorrido' }
        ]
        isWriteMode.value = false
        pushMessage('assistant', 'Que tipo de relatório é este?')
      } else if (field === 'activities') {
        questionOptions.value = []
        isWriteMode.value = true
        pushMessage('assistant', 'O que aconteceu hoje? Descreva as atividades realizadas.')
        nextTick(() => textareaRef.value?.focus())
      }
    }

    const handleOptionClick = async (opt: { label: string; value: string }) => {
      pushMessage('user', opt.label)

      // Confirmação de abertura de chamado (Joe → abrir_ticket)
      if (pendingTicket.value) {
        const ticket = pendingTicket.value
        pendingTicket.value = null
        questionOptions.value = []
        chatState.value = 'idle'
        if (opt.value === '__ticket_yes') {
          try {
            await apiFetch('/tickets', {
              method: 'POST',
              body: JSON.stringify({
                title: ticket.title,
                category: ticket.category,
                description: ticket.description,
                priority: ticket.priority
              })
            })
            pushMessage('assistant', `Chamado "${ticket.title}" aberto! Você pode acompanhá-lo na tela de Chamados.`)
          } catch (err: unknown) {
            const m = err instanceof Error ? err.message : 'Erro desconhecido'
            pushMessage('assistant', `Não consegui abrir o chamado: ${m}`)
          }
        } else {
          pushMessage('assistant', 'Tudo bem, não abri o chamado.')
        }
        return
      }

      const field = currentMissingField.value

      if (field === 'project') {
        if (currentFlow.value === 'add_task') {
          ganttTargetProjectName.value = opt.value
          currentMissingField.value = null
          questionOptions.value = []
          showGanttConfirm()
          return
        }
        pendingFormData.value.project = opt.value
        if (currentFlow.value === 'view') {
          searchAndShowSubmission(opt.value, pendingFormData.value.date as string | null, null)
          return
        }
      } else if (field === 'type') {
        pendingFormData.value.type = opt.value
      }

      missingQueue.value = missingQueue.value.slice(1)
      askNextMissing()
    }

    const enterWriteMode = () => {
      isWriteMode.value = true
      nextTick(() => textareaRef.value?.focus())
    }

    const handleWriteModeInput = (text: string) => {
      const field = currentMissingField.value

      if (field === 'activities') {
        pendingFormData.value.activities = [{ id: makeFormId(), description: text, images: [] }]
        missingQueue.value = missingQueue.value.slice(1)
        isWriteMode.value = false
        askNextMissing()
        return
      }

      if (field === 'project') {
        const lower = text.toLowerCase()
        const match = allProjects.value.find((p: { id: string; name: string }) =>
          p.name.toLowerCase().includes(lower) || lower.includes(p.name.toLowerCase())
        )
        if (match && currentFlow.value === 'add_task') {
          ganttTargetProjectName.value = match.name
          currentMissingField.value = null
          questionOptions.value = []
          isWriteMode.value = false
          showGanttConfirm()
          return
        }
        if (match) {
          pendingFormData.value.project = match.name
          if (currentFlow.value === 'view') {
            searchAndShowSubmission(match.name, pendingFormData.value.date as string | null, null)
            return
          }
          missingQueue.value = missingQueue.value.slice(1)
          isWriteMode.value = false
          askNextMissing()
        } else {
          pushMessage('assistant', 'Não encontrei esse projeto. Escolha da lista:')
          isWriteMode.value = false
          questionOptions.value = allProjects.value.map((p: { id: string; name: string }) => ({ label: p.name, value: p.name }))
        }
        return
      }

      if (field === 'type') {
        const lower = text.toLowerCase()
        let cat: 'relatorio' | 'ocorrido' | null = null
        if (lower.includes('diár') || lower.includes('diar') || lower.includes('relat')) cat = 'relatorio'
        else if (lower.includes('ocorr') || lower.includes('incid')) cat = 'ocorrido'

        if (cat) {
          pendingFormData.value.type = cat
          missingQueue.value = missingQueue.value.slice(1)
          isWriteMode.value = false
          askNextMissing()
        } else {
          pushMessage('assistant', 'Não entendi. Escolha o tipo:')
          isWriteMode.value = false
          questionOptions.value = [
            { label: 'Relatório Diário', value: 'relatorio' },
            { label: 'Registro de Ocorrência', value: 'ocorrido' }
          ]
        }
        return
      }
    }

    // ── VIEW FLOW ──
    const searchAndShowSubmission = async (projectName: string | null, date: string | null, type: string | null) => {
      if (submissionsStore.state.items.length === 0) await submissionsStore.refresh()
      const found = submissionsStore.state.items.find((s: Submission) => {
        if (projectName && !s.attraction.toLowerCase().includes(projectName.toLowerCase())) return false
        if (date && s.reportDate !== date) return false
        if (type && s.category !== type) return false
        return true
      })
      if (found) {
        activeSubmission.value = found
        chatState.value = 'view'
        pushMessage('assistant', 'Aqui está o relatório. Você pode gerar o PDF ou voltar ao chat.')
      } else {
        chatState.value = 'idle'
        currentFlow.value = null
        pushMessage('assistant', 'Não encontrei um relatório com essas informações.')
      }
    }

    // ── PROCESS ANALYZE RESPONSE ──
    const cloneSubmission = (submission: Submission): Submission => JSON.parse(JSON.stringify(submission))

    const formDataFromSubmission = (submission: Submission) => ({
      project: submission.attraction,
      type: submission.category,
      activities: submission.activities,
      observations: submission.observations,
      collaborators: submission.collaborators,
      date: submission.reportDate
    })

    const setEditingContextFromActive = (mode: 'draft' | 'existing') => {
      if (!activeSubmission.value) return
      const current = activeSubmission.value
      editingContext.value = {
        mode,
        submissionId: current.id,
        title: current.title,
        project: current.attraction,
        date: current.reportDate,
        category: current.category === 'ocorrido' ? 'ocorrido' : 'relatorio'
      }
      pendingFormData.value = formDataFromSubmission(current)
    }

    const editActiveSubmission = () => {
      if (!activeSubmission.value) return
      activeSubmission.value = cloneSubmission(activeSubmission.value)
      setEditingContextFromActive('existing')
      chatState.value = 'idle'
      currentFlow.value = 'create'
      isWriteMode.value = false
      pushMessage('assistant', `Estou editando "${activeSubmission.value.title}" (${formatDate(activeSubmission.value.reportDate)}, ${activeSubmission.value.attraction || 'sem projeto'}). O que devo alterar?`)
      nextTick(() => textareaRef.value?.focus())
    }


    const applyLocalEditFallback = (text: string) => {
      if (!activeSubmission.value) return false
      const lower = text.toLowerCase()
      const asItems = text
        .split(/\n|;|\.\s+/)
        .map((item) => item.replace(/^(adicione|inclua|alterar|altere|coloque|registrar|registre)\s+/i, '').trim())
        .filter(Boolean)

      if (lower.includes('observa')) {
        activeSubmission.value.observations = [
          ...activeSubmission.value.observations,
          ...asItems.map((item) => ({ id: makeFormId(), text: item, images: [] }))
        ]
      } else if (lower.includes('colaborador') || lower.includes('equipe')) {
        const names = text
          .replace(/colaboradores?|equipe|adicione|inclua|:/gi, '')
          .split(/,|;|\be\b/i)
          .map((name) => name.trim())
          .filter(Boolean)
        activeSubmission.value.collaborators = Array.from(new Set([...activeSubmission.value.collaborators, ...names]))
      } else {
        activeSubmission.value.activities = [
          ...activeSubmission.value.activities,
          ...asItems.map((item) => ({ id: makeFormId(), description: item, images: [] }))
        ]
      }

      pendingFormData.value = formDataFromSubmission(activeSubmission.value)
      return true
    }

    const applyReportEditInstruction = async (text: string) => {
      if (!editingContext.value || !activeSubmission.value) return false
      isLoading.value = true
      let applied = false
      try {
        const current = activeSubmission.value as Submission
        const makeId = () => newId()
        const res = await apiFetch<EditResponse>('/ia/edit', {
          method: 'POST',
          body: JSON.stringify({
            instruction: text,
            report: {
              reportDate: current.reportDate,
              attraction: current.attraction,
              category: current.category,
              activities: current.activities.map((a) => a.description),
              observations: current.observations.map((o) => o.text),
              collaborators: current.collaborators,
              tools: current.tools.map((t) => t.text)
            }
          })
        })
        const c = res.changes || {}
        if (Object.keys(c).length > 0) {
          if (c.reportDate) current.reportDate = c.reportDate
          if (c.attraction) current.attraction = c.attraction
          if (c.category) current.category = c.category
          if (c.activities) current.activities = c.activities.map((d, i) => ({ id: makeId() + i, description: d, images: [] }))
          if (c.observations) current.observations = c.observations.map((t, i) => ({ id: makeId() + i, text: t, images: [] }))
          if (c.collaborators) current.collaborators = c.collaborators
          if (c.tools) current.tools = c.tools.map((t, i) => ({ id: makeId() + i, text: t, images: [] }))
          const [y, m, d] = current.reportDate.split('-')
          const typeLabel = current.category === 'relatorio' ? 'Relatório Diário' : 'Registro de Ocorrência'
          current.title = `${typeLabel} — ${d || ''}/${m || ''}/${y || ''}`
          applied = true
        }
      } catch { /* ignore, fallback below */ }
      if (!applied) applied = applyLocalEditFallback(text)
      isLoading.value = false
      setEditingContextFromActive(editingContext.value.mode)
      chatState.value = 'confirm'
      pushMessage('assistant', applied
        ? 'Atualizei o relatório. Confira o painel e me diga se quer ajustar mais alguma coisa.'
        : 'Não consegui identificar o que alterar. Tente ser mais específico, por exemplo: "mude a data para hoje" ou "adicione colaborador João".')
      return true
    }

    // ── ADD TASKS TO GANTT FLOW ──
    const showGanttConfirm = () => {
      const total = pendingGanttTasks.value.length
      const areas = Array.from(new Set(pendingGanttTasks.value.map(t => t.area).filter(Boolean))) as string[]
      chatState.value = 'gantt-confirm'
      let msg = `Vou adicionar ${total} atividade${total !== 1 ? 's' : ''} ao planejamento de ${ganttTargetProjectName.value || 'projeto'}`
      if (areas.length > 0) msg += ` — área${areas.length !== 1 ? 's' : ''}: ${areas.join(', ')}`
      pushMessage('assistant', `${msg}. Confira os detalhes ao lado — posso confirmar?`)
    }

    const confirmAddGanttTasks = async () => {
      const project = allProjects.value.find(p => p.name === ganttTargetProjectName.value)
      if (!project) {
        pushMessage('assistant', 'Não consegui identificar o projeto. Tente novamente informando o nome do projeto.')
        resetToIdle()
        return
      }
      ganttSaving.value = true
      try {
        const res = await apiFetch<{ ok: boolean; created: Array<{ id: string; title: string; groupName: string | null }>; groupsCreated: string[] }>(
          `/projects/${encodeURIComponent(project.id)}/ia-add-tasks`,
          {
            method: 'POST',
            body: JSON.stringify({
              tasks: pendingGanttTasks.value.map(t => ({
                title: t.title,
                area: t.area || '',
                startDate: t.startDate || '',
                durationDays: t.durationDays || 1,
                assigneeUsername: t.assigneeUsername || ''
              }))
            })
          }
        )
        const n = res.created.length
        const g = res.groupsCreated.length
        const undatedCount = pendingGanttTasks.value.filter(t => !t.startDate).length
        let msg = `Pronto! Adicionei ${n} atividade${n !== 1 ? 's' : ''} ao planejamento de ${project.name}`
        if (g > 0) msg += ` e criei ${g} grupo${g !== 1 ? 's' : ''} novo${g !== 1 ? 's' : ''}: ${res.groupsCreated.join(', ')}`
        msg += '.'
        if (undatedCount > 0) msg += ` ${undatedCount} ${undatedCount !== 1 ? 'ficaram' : 'ficou'} na lista "Sem data definida" — você pode agendar depois no Gantt.`
        pushMessage('assistant', msg)
        resetToIdle()
      } catch (err: unknown) {
        const m = err instanceof Error ? err.message : 'Erro desconhecido'
        pushMessage('assistant', `Não consegui adicionar as atividades: ${m}`)
      } finally {
        ganttSaving.value = false
      }
    }

    const cancelGanttTasks = () => {
      pushMessage('assistant', 'Tudo bem, não adicionei nada ao planejamento.')
      resetToIdle()
    }

    // ── TICKET CONFIRM (ação abrir_ticket do Joe) ──
    const pendingTicket = ref<{ title: string; category: string; description: string; priority: string } | null>(null)

    const processAnalyzeResponse = async (res: AnalyzeResponse) => {
      if (res.projects) allProjects.value = res.projects

      if (res.intent === 'chat') {
        pushMessage('assistant', res.chatMessage || 'Entendido!')
        if (res.suggest_annotate) {
          pendingAnnotationText.value = res.chatMessage || null
        }
        return
      }

      if (res.intent === 'create_ticket' && res.ticket) {
        pendingTicket.value = res.ticket
        chatState.value = 'filling'
        currentMissingField.value = null
        isWriteMode.value = false
        questionOptions.value = [
          { label: 'Abrir chamado', value: '__ticket_yes' },
          { label: 'Cancelar', value: '__ticket_no' }
        ]
        const prio = res.ticket.priority !== 'media' ? `, prioridade ${res.ticket.priority}` : ''
        pushMessage('assistant', `Vou abrir o chamado "${res.ticket.title}" (categoria: ${res.ticket.category}${prio}). Posso confirmar?`)
        return
      }

      if (res.intent === 'copy') {
        const cq = res.copyQuery || { projectName: null, date: null, type: null }
        if (res.missing && res.missing.includes('project') && (res.projects || []).length > 1) {
          pushMessage('assistant', 'De qual projeto você quer copiar o relatório?')
          questionOptions.value = (res.projects || []).map(p => ({ label: p.name, value: p.name }))
          pendingFormData.value = { ...cq, _flow: 'copy' }
          chatState.value = 'filling'
          currentMissingField.value = 'project'
          return
        }
        pushMessage('assistant', `Buscando relatório de ${cq.projectName || 'projeto'} para copiar...`)
        isLoading.value = true
        try {
          await searchAndShowSubmission(cq.projectName, cq.date, cq.type)
          if (activeSubmission.value) {
            pushMessage('assistant', 'Abri o relatório de origem. Posso criar um novo relatório com as mesmas informações?')
            currentFlow.value = 'create'
          }
        } finally {
          isLoading.value = false
        }
        return
      }

      if (res.intent === 'view') {
        currentFlow.value = 'view'
        const vq = res.viewQuery || { projectName: null, date: null, type: null }
        if (res.missing && res.missing.length > 0) {
          pendingFormData.value = { date: vq.date }
          missingQueue.value = res.missing
          askNextMissing()
        } else {
          await searchAndShowSubmission(vq.projectName || null, vq.date || null, vq.type || null)
        }
        return
      }

      if (res.intent === 'add_task') {
        currentFlow.value = 'add_task'
        pendingGanttTasks.value = res.ganttTasks || []
        ganttTargetProjectName.value = res.projectName || null

        if (res.missing && res.missing.includes('tasks')) {
          pushMessage('assistant', 'Entendi que você quer adicionar atividades ao planejamento, mas não consegui identificar quais. Descreva, por exemplo: "adicione pintura das paredes e troca do piso na garagem".')
          currentFlow.value = null
          return
        }

        if (res.missing && res.missing.includes('project') && (res.projects || []).length > 1) {
          chatState.value = 'filling'
          currentMissingField.value = 'project'
          isWriteMode.value = false
          questionOptions.value = (res.projects || []).map(p => ({ label: p.name, value: p.name }))
          pushMessage('assistant', 'Em qual projeto devo adicionar essas atividades?')
          return
        }

        if (!ganttTargetProjectName.value && allProjects.value.length === 1) {
          ganttTargetProjectName.value = allProjects.value[0].name
        }
        showGanttConfirm()
        return
      }

      if (res.intent === 'create') {
        currentFlow.value = 'create'
        const fd = res.formData
        if (fd) {
          if (fd.projectName) pendingFormData.value.project = fd.projectName
          if (fd.type) pendingFormData.value.type = fd.type
          if (fd.activities?.length) pendingFormData.value.activities = fd.activities
          if (fd.observations?.length) pendingFormData.value.observations = fd.observations
          if (fd.collaborators?.length) pendingFormData.value.collaborators = fd.collaborators
          if (fd.date) pendingFormData.value.date = fd.date
        }

        if (res.missing && res.missing.length > 0) {
          missingQueue.value = res.missing
          askNextMissing()
        } else {
          buildAndConfirm()
        }
      }
    }

    // ── SEND MESSAGE ──
    const activeSession = () => chatSessions.value.find((c: ChatSession) => c.id === activeChatId.value)

    const sendMessage = async () => {
      const text = inputText.value.trim()
      if (!text && attachments.value.length === 0) return

      // Memória rolante: envia só o resumo + mensagens ainda não "dobradas"
      // (capturado ANTES de anexar a mensagem nova, que vai no campo message)
      const sess = activeSession()
      const memorySummary = sess?.memory || ''
      const memoryCount = sess?.memoryCount || 0
      const recentPayload = messages.value
        .slice(memoryCount)
        .slice(-12)
        .map((m: ChatMessage) => ({ role: m.role, content: m.content }))

      pushMessage('user', text || '[arquivo]')
      inputText.value = ''
      attachments.value = []
      nextTick(() => { if (textareaRef.value) textareaRef.value.style.height = 'auto' })

      // Annotation suggestion: waiting for sim/não
      if (pendingAnnotationText.value !== null) {
        const answer = text.toLowerCase().trim()
        const isYes = ['sim', 's', 'yes', 'y', 'ok', 'pode', 'claro', 'vai', 'anote', 'anota'].some(w => answer.startsWith(w))
        const isNo = ['não', 'nao', 'n', 'no', 'não precisa', 'deixa', 'cancela'].some(w => answer.startsWith(w))
        if (isYes) {
          const content = pendingAnnotationText.value
          pendingAnnotationText.value = null
          try {
            await annotationsStore.create({ content })
            pushMessage('assistant', 'Anotação salva! Você pode acessá-la em Anotações no menu do perfil.')
          } catch {
            pushMessage('assistant', 'Não consegui salvar a anotação. Tente novamente mais tarde.')
          }
          return
        } else if (isNo) {
          pendingAnnotationText.value = null
          pushMessage('assistant', 'Tudo bem! Se precisar de mais alguma coisa, é só falar.')
          return
        }
        // Not a clear yes/no — clear the pending and process normally
        pendingAnnotationText.value = null
      }

      // Filling + write mode: handle without AI
      if (chatState.value === 'filling' && isWriteMode.value) {
        handleWriteModeInput(text)
        return
      }

      if (editingContext.value && activeSubmission.value) {
        await applyReportEditInstruction(text)
        return
      }

      isLoading.value = true
      try {
        const res = await apiFetch<AnalyzeResponse>('/ia/assistant', {
          method: 'POST',
          body: JSON.stringify({ message: text, summary: memorySummary, recent: recentPayload })
        })
        // Backend dobrou mensagens antigas no resumo → persiste na sessão
        if (res.memory && res.memory.summary) {
          const s = activeSession()
          if (s) {
            s.memory = res.memory.summary
            s.memoryCount = (s.memoryCount || 0) + res.memory.folded
            saveChatHistory()
          }
        }
        await processAnalyzeResponse(res)
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Erro desconhecido'
        pushMessage('assistant', `Desculpe, ocorreu um erro: ${msg}`)
      } finally {
        isLoading.value = false
      }
    }

    // ── CONFIRM / SAVE ──
    const confirmSave = async () => {
      if (!activeSubmission.value) return
      try {
        if (editingContext.value?.mode === 'existing') {
          await submissionsStore.updateSubmission(activeSubmission.value.id, {
            title: activeSubmission.value.title,
            category: activeSubmission.value.category,
            attraction: activeSubmission.value.attraction,
            reportDate: activeSubmission.value.reportDate,
            collaborators: activeSubmission.value.collaborators,
            tools: activeSubmission.value.tools,
            activities: activeSubmission.value.activities,
            observations: activeSubmission.value.observations,
            reportLanguage: activeSubmission.value.reportLanguage,
            status: activeSubmission.value.status
          })
        } else {
          await submissionsStore.addSubmission({
            ...activeSubmission.value,
            status: 'rascunho',
            author: sessionStore.state.user!
          })
        }
        pushMessage('assistant', editingContext.value?.mode === 'existing' ? 'Relatório atualizado com sucesso!' : 'Relatório salvo com sucesso!')
        resetToIdle()
      } catch {
        pushMessage('assistant', 'Ocorreu um erro ao salvar. Tente novamente.')
      }
    }

    const confirmEdit = () => {
      if (activeSubmission.value) setEditingContextFromActive(editingContext.value?.mode || 'draft')
      chatState.value = 'idle'
      isWriteMode.value = false
      pushMessage('assistant', 'Claro! O que deveria mudar no relatório?')
    }

    const resetToIdle = () => {
      chatState.value = 'idle'
      currentFlow.value = null
      activeSubmission.value = null
      pendingFormData.value = {}
      missingQueue.value = []
      currentMissingField.value = null
      questionOptions.value = []
      isWriteMode.value = false
      editingContext.value = null
      pendingGanttTasks.value = []
      ganttTargetProjectName.value = null
    }

    // ── PDF EXPORT ──
    const exportPdf = async () => {
      if (!activeSubmission.value) return
      pdfDocs.value = [activeSubmission.value]
      pdfSelected.value = [activeSubmission.value.id]
      try {
        await exportSelectedDocsAsPdf('pt')
      } catch {
        pushMessage('assistant', 'Ocorreu um erro ao gerar o PDF.')
      }
    }

    // ── INPUT HELPERS ──
    const autoResize = () => {
      const el = textareaRef.value
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 200) + 'px'
    }

    const fillSuggestion = (text: string) => {
      inputText.value = text
      nextTick(() => { autoResize(); textareaRef.value?.focus() })
    }

    const onFileChange = (e: Event) => {
      const files = (e.target as HTMLInputElement).files
      if (!files) return
      attachments.value = [...attachments.value, ...Array.from(files)]
      if (fileInputRef.value) fileInputRef.value.value = ''
    }

    const removeAttachment = (i: number) => { attachments.value.splice(i, 1) }

    // ── WEB SPEECH API ──
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type AnySpeechRecognition = any
    const STT_LANG_KEY = 'ia-stt-lang'
    const sttLang = ref(localStorage.getItem(STT_LANG_KEY) || 'pt-BR')
    watch(sttLang, (v: string) => localStorage.setItem(STT_LANG_KEY, v))

    const w = window as Record<string, AnySpeechRecognition>
    const SpeechRecognitionCtor: AnySpeechRecognition = w['SpeechRecognition'] || w['webkitSpeechRecognition'] || null
    const sttSupported = !!SpeechRecognitionCtor
    let recognition: AnySpeechRecognition = null

    const toggleRecording = () => { if (isRecording.value) stopRecording(); else startRecording() }

    const startRecording = () => {
      if (!SpeechRecognitionCtor) return
      recognition = new SpeechRecognitionCtor()
      recognition.lang = sttLang.value
      recognition.continuous = false
      recognition.interimResults = false

      recognition.onstart = () => {
        isRecording.value = true
        recordingTime.value = 0
        recordingInterval = setInterval(() => { recordingTime.value++ }, 1000)
      }

      recognition.onresult = (event: AnySpeechRecognition) => {
        const transcript = Array.from(event.results as AnySpeechRecognition[])
          .map((r: AnySpeechRecognition) => r[0].transcript as string)
          .join('')
        inputText.value = (inputText.value ? inputText.value + ' ' : '') + transcript
        nextTick(() => autoResize())
      }

      recognition.onerror = () => { stopRecording() }
      recognition.onend = () => { stopRecording() }
      recognition.start()
    }

    const stopRecording = () => {
      isRecording.value = false
      recordingTime.value = 0
      if (recordingInterval) { clearInterval(recordingInterval); recordingInterval = null }
      if (recognition) { recognition.stop(); recognition = null }
    }

    const cancelRecording = () => {
      if (recognition) { recognition.abort(); recognition = null }
      isRecording.value = false
      recordingTime.value = 0
      if (recordingInterval) { clearInterval(recordingInterval); recordingInterval = null }
    }

    onMounted(async () => {
      loadTrash()
      // Sempre abre em conversa nova (como chatbots comuns): o histórico carrega
      // apenas a sidebar; a conversa ativa começa vazia e só vira sessão ao
      // enviar a primeira mensagem.
      await loadChatHistory()

      // Limpa rascunhos legados persistidos por versões antigas do painel.
      try { localStorage.removeItem(`ia-draft-${sessionStore.state.user?.systemId || 'unknown'}`) } catch { /* ignore */ }

      try {
        if (projectsStore.state.items.length === 0) await projectsStore.refresh()
        allProjects.value = projectsStore.state.items.map((p: { id: string; name: string }) => ({ id: p.id, name: p.name }))
      } catch { /* non-fatal */ }
    })

    onBeforeUnmount(() => {
      syncActiveChat()
      if (recordingInterval) clearInterval(recordingInterval)
    })

    return {
      goBack,
      inputText, textareaRef, fileInputRef,
      attachments, isRecording, recordingTime,
      messages, messagesBodyRef, isLoading,
      chatSessions, activeChatId, historySearch, filteredChats,
      allProjects,
      chatState, activeSubmission, questionOptions, isWriteMode,
      canScrollUp, canScrollDown,
      showTextarea, questionPlaceholder,
      autoResize, fillSuggestion, sendMessage,
      onFileChange, removeAttachment, toggleRecording, cancelRecording,
      confirmSave, confirmEdit, resetToIdle, exportPdf,
      handleOptionClick, enterWriteMode,
      categoryLabel, formatDate, onBodyScroll,
      closePanel,
      startNewChat, selectChat, deleteChat, formatHistoryTime, editActiveSubmission,
      trashedSessions, trashOpen, deletePendingId, confirmDelete, restoreChat, purgeChat,
      sidebarOpen, textareaFocused,
      sttLang, sttSupported,
      ganttReportMode, offlineProjectId, offlineWeekOffset, offlineGenerating, offlineError, generateOfflineReport,
      pendingGanttTasks, ganttTargetProjectName, ganttSaving, ganttPanelVisible, ganttTasksByArea,
      confirmAddGanttTasks, cancelGanttTasks, currentUserName,
    }
  }
})
</script>

<style scoped lang="scss">
/* ── IA-SPECIFIC DESIGN TOKENS (light defaults) ── */
.ia-shell {
  // Light mode tokens
  --ia-bg:            oklch(0.975 0.006 256);
  --ia-surface:       oklch(1 0 0);
  --ia-surface-2:     oklch(0.965 0.006 256);
  --ia-line:          oklch(0.25 0.04 256 / 0.12);
  --ia-line-soft:     oklch(0.25 0.04 256 / 0.07);
  --ia-ink:           oklch(0.24 0.04 256);
  --ia-muted:         oklch(0.24 0.04 256 / 0.62);
  --ia-faint:         oklch(0.24 0.04 256 / 0.42);
  --ia-accent:        oklch(0.64 0.15 52);
  --ia-accent-2:      oklch(0.58 0.16 40);
  --ia-accent-soft:   oklch(0.64 0.15 52 / 0.15);
  --ia-send-fg:       oklch(0.99 0.01 90);
  --ia-glow1:         oklch(0.8 0.145 70 / 0.12);
  --ia-glow2:         oklch(0.55 0.09 256 / 0.07);
  --ia-shadow:        oklch(0.3 0.05 256 / 0.12);
  --ia-hover:         oklch(0.25 0.04 256 / 0.04);
  --ia-hover-strong:  oklch(0.25 0.04 256 / 0.07);
  --ia-chip-bg:       oklch(0.25 0.04 256 / 0.02);
  --ia-font-title:    "Bebas Neue", "Archivo", sans-serif;
  --ia-font-head:     "Archivo", system-ui, sans-serif;

  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--navbar-h));
  background: var(--ia-bg);
  color: var(--ia-ink);
  overflow: hidden;
}

/* ── DARK MODE TOKENS ── */
[data-theme="dark"] .ia-shell {
  --ia-bg:            oklch(0.18 0.032 256);
  --ia-surface:       oklch(0.235 0.034 256);
  --ia-surface-2:     oklch(0.275 0.036 256);
  --ia-line:          oklch(1 0 0 / 0.08);
  --ia-line-soft:     oklch(1 0 0 / 0.05);
  --ia-ink:           oklch(0.97 0.008 250);
  --ia-muted:         oklch(0.97 0.008 250 / 0.58);
  --ia-faint:         oklch(0.97 0.008 250 / 0.36);
  --ia-accent:        oklch(0.8 0.145 70);
  --ia-accent-2:      oklch(0.72 0.16 48);
  --ia-accent-soft:   oklch(0.8 0.145 70 / 0.14);
  --ia-send-fg:       oklch(0.2 0.04 60);
  --ia-glow1:         oklch(0.8 0.145 70 / 0.1);
  --ia-glow2:         oklch(0.55 0.09 250 / 0.16);
  --ia-shadow:        oklch(0 0 0 / 0.35);
  --ia-hover:         oklch(1 0 0 / 0.04);
  --ia-hover-strong:  oklch(1 0 0 / 0.06);
  --ia-chip-bg:       oklch(1 0 0 / 0.02);
}

/* ── CONTENT ROW ── */
.ia-content-row {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

/* ── LEFT COLUMN ── */
.ia-history-sidebar {
  width: 286px;
  flex: 0 0 286px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--ia-surface-2);
  border-right: 1px solid var(--ia-line);
}

.ia-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px 16px;
}

.ia-brand-badge {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: var(--ia-surface);
  border: 1px solid var(--ia-line);
  display: grid;
  place-items: center;
  box-shadow: 0 2px 10px var(--ia-shadow);
}

.ia-brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1;
  min-width: 0;
}

.ia-brand-name {
  font-family: var(--ia-font-title);
  font-size: 26px;
  letter-spacing: 0.04em;
  color: var(--ia-ink);
}

.ia-brand-sub {
  margin-top: 4px;
  font-family: var(--ia-font-head);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ia-faint);
}

.ia-new-chat {
  margin: 4px 16px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 11px 14px;
  border-radius: 12px;
  border: 1px solid var(--ia-accent-soft);
  background: linear-gradient(180deg, var(--ia-accent-soft), transparent);
  color: var(--ia-ink);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.15s ease;

  &:hover {
    border-color: var(--ia-accent);
    transform: translateY(-1px);
  }

  svg {
    width: 17px;
    height: 17px;
    color: var(--ia-accent);
  }
}

.ia-history-search {
  margin: 0 16px 14px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  border-radius: 10px;
  background: var(--ia-bg);
  border: 1px solid var(--ia-line-soft);

  svg {
    width: 15px;
    height: 15px;
    color: var(--ia-faint);
    flex: none;
  }

  input {
    width: 100%;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: var(--ia-ink);
    font: inherit;
    font-size: 13.5px;

    &::placeholder { color: var(--ia-faint); }
  }
}

.ia-history-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 8px 12px;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: oklch(0.5 0 0 / 0.12); border-radius: 8px; }
}

.ia-history-label {
  padding: 14px 12px 7px;
  font-family: var(--ia-font-head);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ia-faint);
}

.ia-history-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 9px;
  background: transparent;
  color: var(--ia-muted);
  text-align: left;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;

  &:hover {
    background: var(--ia-hover);
    color: var(--ia-ink);

    .ia-history-delete { opacity: 1; }
  }

  &.active {
    background: var(--ia-hover-strong);
    color: var(--ia-ink);
  }
}

.ia-history-delete {
  flex: none;
  margin-left: auto;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--ia-faint);
  display: grid;
  place-items: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s, background 0.15s, color 0.15s;

  &:hover {
    background: oklch(0.55 0.18 25 / 0.12);
    color: oklch(0.55 0.18 25);
  }
}

.ia-history-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ia-accent);
  flex: none;
  opacity: 0;
}

.ia-history-item.active .ia-history-dot { opacity: 1; }

.ia-history-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.ia-history-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13.5px;
  font-weight: 600;
}

.ia-history-meta {
  font-size: 11.5px;
  color: var(--ia-faint);
}

.ia-history-empty {
  padding: 9px 12px;
  font-size: 12.5px;
  color: var(--ia-faint);
}

/* ── TRASH SECTION ── */
.ia-trash-section {
  border-top: 1px solid var(--ia-line-soft);
  margin-top: 6px;
}

.ia-trash-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 14px;
  background: none;
  border: none;
  color: var(--ia-faint);
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  letter-spacing: 0.04em;
  transition: color 0.15s;

  &:hover { color: var(--ia-muted); }

  svg:first-child { flex: none; }
}

.ia-trash-count {
  margin-left: 2px;
  background: var(--ia-hover-strong);
  color: var(--ia-muted);
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 100px;
}

.ia-trash-chevron {
  margin-left: auto;
  flex: none;
  transition: transform 0.2s;
  &.open { transform: rotate(180deg); }
}

.ia-trash-list {
  padding: 0 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ia-trash-item {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 1px 8px;
  padding: 7px 10px;
  border-radius: 8px;
  background: var(--ia-hover);
  align-items: center;
}

.ia-trash-item-title {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ia-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  grid-row: 1;
  grid-column: 1;
}

.ia-trash-item-meta {
  font-size: 11px;
  color: var(--ia-faint);
  grid-row: 2;
  grid-column: 1;
}

.ia-trash-item-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  grid-row: 1 / 3;
  grid-column: 2;
}

.ia-trash-restore,
.ia-trash-purge {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.ia-trash-restore {
  color: var(--ia-accent);
  &:hover { background: oklch(0.52 0.18 220 / 0.14); }
}

.ia-trash-purge {
  color: var(--ia-faint);
  &:hover { background: oklch(0.55 0.18 25 / 0.12); color: oklch(0.55 0.18 25); }
}

/* ── DELETE CONFIRM DIALOG ── */
.ia-delete-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.ia-delete-dialog {
  background: var(--ia-surface, #1e1e1e);
  border: 1px solid var(--ia-line-soft, rgba(255,255,255,0.08));
  border-radius: 16px;
  padding: 28px 28px 24px;
  max-width: 360px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
}

.ia-delete-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: oklch(0.55 0.18 25 / 0.12);
  color: oklch(0.65 0.18 25);
  display: grid;
  place-items: center;
  margin-bottom: 4px;
}

.ia-delete-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ia-ink, #f0ede8);
  margin: 0;
}

.ia-delete-sub {
  font-size: 12.5px;
  color: var(--ia-faint, rgba(240,237,232,0.4));
  margin: 0;
  line-height: 1.5;
}

.ia-delete-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
  width: 100%;
}

.ia-delete-cancel,
.ia-delete-confirm {
  flex: 1;
  padding: 9px 0;
  border-radius: 9px;
  border: none;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.85; }
}

.ia-delete-cancel {
  background: var(--ia-hover, rgba(255,255,255,0.06));
  color: var(--ia-muted, rgba(240,237,232,0.7));
}

.ia-delete-confirm {
  background: oklch(0.55 0.18 25);
  color: #fff;
}

.ia-stt-config {
  padding: 12px 16px 16px;
  border-top: 1px solid var(--ia-line-soft);
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.ia-stt-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ia-faint);

  svg { flex: none; }
}

.ia-stt-select {
  width: 100%;
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid var(--ia-line);
  background: var(--ia-bg);
  color: var(--ia-ink);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.18s;

  &:focus { border-color: var(--ia-accent); }
}

.ia-stt-warn {
  font-size: 11.5px;
  color: oklch(0.55 0.18 25);
}

.ia-left {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: -1;
    background:
      radial-gradient(60% 50% at 78% 8%, var(--ia-glow1), transparent 70%),
      radial-gradient(55% 45% at 12% 100%, var(--ia-glow2), transparent 70%);
  }
}

/* Back button floats over content, no layout cost */
.ia-back-btn {
  position: absolute;
  top: 10px;
  left: 14px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: var(--ia-faint);
  padding: 2px 0;
  transition: color 0.15s;

  &:hover { color: var(--ia-muted); }
}

/* ── BODY WRAPPER (scroll + fades) ── */
.ia-body-wrap {
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.ia-body {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px 24px;

  &--messages {
    justify-content: flex-start;
    padding-top: 40px;
  }

  &::-webkit-scrollbar { width: 10px; }
  &::-webkit-scrollbar-thumb { background: oklch(0.5 0 0 / 0.12); border-radius: 8px; }
}

/* Fade overlays */
.ia-fade {
  position: absolute;
  left: 0;
  right: 0;
  height: 52px;
  pointer-events: none;
  z-index: 5;
  transition: opacity 0.2s;
}

.ia-fade--top {
  top: 0;
  background: linear-gradient(to bottom, var(--ia-bg) 20%, transparent);
}

.ia-fade--bottom {
  bottom: 0;
  background: linear-gradient(to top, var(--ia-bg) 20%, transparent);
}

/* ── HERO ── */
.ia-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 640px;
  width: 100%;
  padding: 0 8px 32px;
}

.ia-welcome-avatar {
  width: 66px;
  height: 66px;
  border-radius: 18px;
  background: var(--ia-surface);
  border: 1px solid var(--ia-line);
  display: grid;
  place-items: center;
  margin-bottom: 22px;
  box-shadow: 0 10px 40px var(--ia-glow1), 0 2px 12px oklch(0 0 0 / 0.18), inset 0 0 0 1px oklch(0 0 0 / 0.04);
}

.ia-welcome-title {
  font-family: var(--ia-font-title);
  font-weight: 400;
  font-size: clamp(40px, 5.5vw, 62px);
  letter-spacing: 0.02em;
  line-height: 0.95;
  color: var(--ia-ink);
  margin: 0 0 14px;
}

.ia-hero-desc {
  font-size: 16px;
  color: var(--ia-muted);
  margin: 0 0 24px;
  line-height: 1.55;
  max-width: 46ch;
  text-wrap: pretty;
}

/* ── Hero suggestion chips ── */
.ia-hero-suggestions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: -8px;
}
.ia-suggestion-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px solid var(--ia-line);
  border-radius: 99px;
  background: var(--ia-surface);
  color: var(--ia-muted);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
}
.ia-suggestion-chip:hover {
  border-color: var(--ia-accent);
  color: var(--ia-ink);
  background: var(--ia-accent-soft);
}

/* ── Offline report form ── */
.offline-report-form {
  display: flex; flex-direction: column; gap: 12px;
  width: 100%; max-width: 360px;
}
.offline-report-field { display: flex; flex-direction: column; gap: 5px; }
.offline-report-label { font-size: 12px; font-weight: 600; color: var(--ia-muted); letter-spacing: 0.04em; text-transform: uppercase; }
.offline-report-select {
  border: 1.5px solid var(--ia-line); border-radius: 8px;
  padding: 8px 12px; font-size: 14px; background: var(--ia-surface); color: var(--ia-ink);
  outline: none; width: 100%;
  &:focus { border-color: var(--ia-accent, #0f4c81); }
}
.offline-report-error { font-size: 13px; color: #dc2626; margin: 0; }
.offline-report-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px 20px; border-radius: 9px; font-size: 14px; font-weight: 600;
  background: var(--ia-accent, #0f4c81); color: #fff; border: none; cursor: pointer;
  transition: filter .15s;
  &:hover:not(:disabled) { filter: brightness(1.1); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

/* Loading dot for offline button */
.ia-loading-dot {
  display: inline-block; width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff;
  animation: ia-spin 0.7s linear infinite;
}
@keyframes ia-spin { to { transform: rotate(360deg); } }

.ia-suggest-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  max-width: 600px;
}

.ia-suggest {
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  padding: 15px 16px;
  border-radius: 14px;
  border: 1px solid var(--ia-line);
  background: var(--ia-surface);
  cursor: pointer;
  transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    border-color: var(--ia-accent);
    transform: translateY(-2px);
    box-shadow: 0 12px 30px var(--ia-shadow);
  }
}

.ia-suggest-icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  flex: none;
  display: grid;
  place-items: center;
  background: var(--ia-accent-soft);
  color: var(--ia-accent);

  svg { width: 19px; height: 19px; }
}

.ia-suggest-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  b {
    font-family: var(--ia-font-head);
    font-weight: 600;
    font-size: 14px;
    color: var(--ia-ink);
  }

  span {
    font-size: 12.5px;
    color: var(--ia-muted);
  }
}

/* ── MESSAGES ── */
.ia-messages {
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 100%;
  max-width: 760px;
  padding: 8px 0 16px;
}

.ia-msg {
  display: flex;
  gap: 15px;

  &--user {
    justify-content: flex-end;
  }
}

.ia-bot-avatar {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  flex: none;
  background: var(--ia-surface);
  border: 1px solid var(--ia-line);
  display: grid;
  place-items: center;
  box-shadow: 0 2px 10px oklch(0 0 0 / 0.15);
  margin-top: 2px;
}

.ia-msg-body {
  min-width: 0;
  padding-top: 2px;
}

.ia-msg-name {
  font-family: var(--ia-font-head);
  font-size: 13px;
  font-weight: 600;
  color: var(--ia-ink);
  margin-bottom: 7px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ia-msg-tag {
  font-family: var(--ia-font-head);
  font-size: 10px;
  font-weight: 600;
  color: var(--ia-accent);
  background: var(--ia-accent-soft);
  padding: 2px 7px;
  border-radius: 999px;
  letter-spacing: 0.04em;
}

.ia-msg-text {
  font-size: 15px;
  line-height: 1.62;
  color: var(--ia-ink);
  white-space: pre-wrap;
  word-break: break-word;
}

.ia-user-bubble {
  background: linear-gradient(180deg, var(--ia-surface-2), var(--ia-surface));
  border: 1px solid var(--ia-line);
  color: var(--ia-ink);
  padding: 13px 17px;
  border-radius: 16px 16px 5px 16px;
  font-size: 15px;
  line-height: 1.55;
  max-width: 78%;
  white-space: pre-wrap;
  word-break: break-word;
}

/* ── INPUT AREA ── */
.ia-input-area {
  padding: 8px 24px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.ia-input-wrap {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  background: var(--ia-surface);
  border: 1px solid var(--ia-line);
  border-radius: 20px;
  padding: 11px 11px 11px 16px;
  width: 100%;
  max-width: 760px;
  box-shadow: 0 12px 40px var(--ia-shadow);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus-within {
    border-color: var(--ia-accent);
    box-shadow: 0 12px 50px var(--ia-shadow), 0 0 0 4px var(--ia-accent-soft);
  }
}

.ia-tool-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: var(--ia-muted);
  display: grid;
  place-items: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.16s ease, color 0.16s ease;

  &:hover { background: var(--ia-hover-strong); color: var(--ia-ink); }

  svg { width: 19px; height: 19px; }

  &--recording {
    color: #dc2626;
    background: rgba(220, 38, 38, 0.1);
    animation: rec-pulse 1.2s infinite;
    &:hover { background: rgba(220, 38, 38, 0.18); }
  }
}

@keyframes rec-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.ia-textarea {
  flex: 1;
  resize: none;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--ia-font-body, inherit);
  font-size: 15px;
  color: var(--ia-ink);
  line-height: 1.5;
  padding: 8px 4px;
  max-height: 160px;
  min-height: 24px;
  overflow-y: auto;

  &::placeholder { color: var(--ia-faint); }
  &:disabled { opacity: 0.5; }
}

.ia-send-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: none;
  background: var(--ia-accent-soft);
  color: var(--ia-accent);
  display: grid;
  place-items: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, transform 0.15s ease, filter 0.15s ease, box-shadow 0.15s ease;
  opacity: 0.5;

  &--active {
    opacity: 1;
    background: linear-gradient(150deg, var(--ia-accent), var(--ia-accent-2));
    color: var(--ia-send-fg);
    box-shadow: 0 4px 16px oklch(0.8 0.145 70 / 0.3);
    &:hover { transform: translateY(-1px) scale(1.03); filter: brightness(1.06); }
  }

  &:disabled { cursor: default; }
}

.ia-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 760px;
  width: 100%;
}

.ia-attachment {
  display: flex;
  align-items: center;
  gap: 5px;
  background: var(--ia-surface);
  border: 1px solid var(--ia-line);
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 12px;
  color: var(--ia-ink);

  svg { flex-shrink: 0; color: var(--ia-accent); }
  span { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}

.ia-attachment-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ia-faint);
  display: flex;
  align-items: center;
  padding: 0 2px;
  border-radius: 3px;
  &:hover { color: #dc2626; }
}

.ia-recording-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #dc2626;
  max-width: 760px;
  width: 100%;
}

.ia-rec-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dc2626;
  flex-shrink: 0;
  animation: rec-pulse 1s infinite;
}

.ia-rec-cancel {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: var(--ia-muted);
  margin-left: auto;
  text-decoration: underline;
  padding: 0;
  &:hover { color: var(--ia-ink); }
}

.ia-disclaimer {
  font-size: 12px;
  color: var(--ia-faint);
  margin: 0;
  text-align: center;
  max-width: 760px;
}

/* ── SIDE PANEL (full height) ── */
.ia-side-panel {
  width: 40%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--ia-surface);
  border-left: 1px solid var(--ia-line);
  overflow: hidden;
}

.ia-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--ia-line-soft);
  flex-shrink: 0;
}

.ia-panel-header-title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: var(--ia-font-head);
  font-size: 13px;
  font-weight: 600;
  color: var(--ia-ink);
  svg { color: var(--ia-accent); }
}

.ia-panel-close {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--ia-faint);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease, border-color 0.16s ease;
  &:hover { background: var(--ia-surface-2); color: var(--ia-ink); border-color: var(--ia-line); }
}

.ia-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: oklch(0.5 0 0 / 0.1); border-radius: 8px; }
}

.ia-panel-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ia-panel-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.ia-panel-label {
  font-family: var(--ia-font-head);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ia-faint);
}

.ia-panel-value {
  font-size: 14px;
  color: var(--ia-ink);
  font-weight: 500;
  line-height: 1.4;
}

.ia-panel-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;

  &--relatorio  { background: var(--ia-accent-soft); color: var(--ia-accent-2); }
  &--dialogo    { background: rgba(16, 185, 129, 0.1); color: #059669; }
  &--permissao  { background: rgba(245, 158, 11, 0.1); color: #d97706; }
  &--ocorrido   { background: rgba(239, 68, 68, 0.1);  color: #dc2626; }
}

.ia-panel-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.ia-panel-tag {
  display: inline-block;
  padding: 2px 8px;
  background: var(--ia-surface-2);
  border: 1px solid var(--ia-line);
  border-radius: 6px;
  font-size: 12px;
  color: var(--ia-ink);
}

/* ── Gantt tasks preview (add_task flow) ── */
.ia-gantt-task {
  padding: 8px 10px;
  border: 1px solid var(--ia-line-soft);
  border-radius: 8px;
  background: var(--ia-surface-2);
  margin-bottom: 6px;
}
.ia-gantt-task-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--ia-ink);
  margin-bottom: 5px;
}
.ia-gantt-task-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.ia-gantt-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 99px;
  background: var(--ia-surface);
  border: 1px solid var(--ia-line);
  font-size: 11px;
  font-weight: 600;
  color: var(--ia-muted);
}
.ia-gantt-chip--nodate {
  background: rgba(154, 107, 0, 0.1);
  border-color: rgba(154, 107, 0, 0.35);
  color: #9a6b00;
}
.ia-gantt-chip--assignee { color: var(--ia-ink); }
.ia-gantt-note {
  font-size: 11.5px;
  color: var(--ia-faint);
  line-height: 1.5;
  margin: 4px 0 0;
}

.ia-panel-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ia-panel-list-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: var(--ia-ink);
  line-height: 1.45;
}

.ia-panel-list-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--ia-accent);
  flex-shrink: 0;
  margin-top: 5px;
}

.ia-panel-author {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--ia-muted);
  padding-top: 4px;
  border-top: 1px solid var(--ia-line-soft);
  svg { flex-shrink: 0; }
}

/* ── ACTION BAR (confirm / view) ── */
.ia-action-bar {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  max-width: 760px;
  width: 100%;
}

.ia-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 20px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-family: var(--ia-font-head);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover { transform: translateY(-1px); box-shadow: 0 4px 14px oklch(0 0 0 / 0.1); }
  &:active { transform: translateY(0); }

  &--save {
    background: linear-gradient(150deg, var(--ia-accent), var(--ia-accent-2));
    color: var(--ia-send-fg);
    box-shadow: 0 4px 14px oklch(0.8 0.145 70 / 0.25);
    &:hover { filter: brightness(1.05); }
  }

  &--edit {
    background: var(--ia-surface);
    border-color: var(--ia-line);
    color: var(--ia-ink);
    &:hover { border-color: var(--ia-muted); }
  }

  &--pdf {
    background: #059669;
    color: #fff;
    &:hover { background: #047857; }
  }

  &--back {
    background: var(--ia-surface);
    border-color: var(--ia-line);
    color: var(--ia-ink);
    &:hover { border-color: var(--ia-muted); }
  }

  &--draft {
    background: var(--ia-surface);
    border-color: oklch(0.64 0.15 52 / 0.4);
    color: oklch(0.58 0.16 40);
    &:hover { background: oklch(0.64 0.15 52 / 0.06); border-color: var(--ia-accent); }
  }
}

/* ── QUESTION BAR (filling state) ── */
.ia-question-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 760px;
  width: 100%;
}

.ia-opt-btn {
  padding: 8px 16px;
  background: var(--ia-surface);
  border: 1px solid var(--ia-line);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ia-muted);
  cursor: pointer;
  transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease, transform 0.15s ease;

  &:hover {
    background: var(--ia-accent-soft);
    border-color: var(--ia-accent);
    color: var(--ia-ink);
    transform: translateY(-1px);
  }
}

.ia-write-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: var(--ia-chip-bg);
  border: 1px solid var(--ia-line);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ia-muted);
  cursor: pointer;
  transition: border-color 0.16s ease, color 0.16s ease;

  &:hover { border-color: var(--ia-accent); color: var(--ia-ink); }
}

.ia-draft-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  background: var(--ia-chip-bg);
  border: 1px solid oklch(0.64 0.15 52 / 0.35);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: oklch(0.58 0.16 40);
  cursor: pointer;
  transition: background 0.16s ease, border-color 0.16s ease;

  &:hover { background: oklch(0.64 0.15 52 / 0.06); border-color: var(--ia-accent); }
}

/* Mobile backdrop (side panel) */
.ia-panel-backdrop { display: none; }

/* Mobile sidebar backdrop */
.ia-sidebar-backdrop { display: none; }

/* Mobile history toggle button — hidden on desktop */
.ia-history-toggle { display: none; }

/* ── MOBILE ── */
@media (max-width: 960px) {
  .ia-back-btn { display: none; }

  .ia-history-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    position: absolute;
    top: 10px;
    left: 14px;
    z-index: 10;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    color: var(--ia-faint);
    padding: 2px 0;
    transition: color 0.15s;

    &:hover { color: var(--ia-muted); }
  }

  .ia-history-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 300;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
    box-shadow: 4px 0 24px oklch(0 0 0 / 0.18);

    &--open {
      transform: translateX(0);
    }
  }

  .ia-sidebar-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: oklch(0 0 0 / 0.35);
    z-index: 299;
  }
}

@media (max-width: 600px) {
  .ia-body { padding: 48px 16px 16px; }
  .ia-input-area { padding: 8px 12px 16px; }
  .ia-welcome-title { font-size: clamp(34px, 10vw, 48px); }
  .ia-suggest-grid { grid-template-columns: 1fr; }

  .ia-side-panel {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 92vw;
    max-height: 80vh;
    border-radius: 16px;
    box-shadow: 0 8px 40px oklch(0 0 0 / 0.3);
    z-index: 200;
    border: none;
  }

  .ia-panel-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: oklch(0 0 0 / 0.4);
    z-index: 199;
  }
}

/* ── INDICADOR "DIGITANDO" ── */
.ia-typing-dots {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 2px 4px;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--ia-accent);
    animation: ia-typing-bounce 1.15s ease-in-out infinite;

    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
}

@keyframes ia-typing-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-4px); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .ia-typing-dots span { animation: ia-typing-fade 1.15s ease-in-out infinite; }
  @keyframes ia-typing-fade {
    0%, 60%, 100% { opacity: 0.4; }
    30% { opacity: 1; }
  }
}
</style>
